import { FC, ReactNode } from 'react'
import {
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  IconButton,
  TextField,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material'
import { ArrowBack, ExitToApp, KeyboardArrowRight, People } from '@mui/icons-material'
import { useNavigate, Navigate } from 'react-router-dom'
import { useFormik } from 'formik'
import { LoadingButton } from '@mui/lab'
import * as Yup from 'yup'
import { useIntl } from 'react-intl'
import { useSnackbar } from 'notistack'

import PasswordField from 'components/PasswordField'
import BackNavigationHeader, { BackNavigationContent } from 'components/BackNavigationHeader'
import {
  useMasquarade,
  useMasquaradeUser,
  useMasquaradeToken
} from 'services/masquarade/masquarade.hooks'
import theme from 'styles/theme'

import { azureButtonSx, profileFieldSx } from 'components/User/userForm.styles'

const MasquaradeLogin: FC = () => {
  const { enqueueSnackbar } = useSnackbar()
  const navigation = useNavigate()
  const { formatMessage } = useIntl()
  const muiTheme = useTheme()
  const isDesktop = useMediaQuery(muiTheme.breakpoints.up('md'))
  const labelBg = isDesktop ? theme.r4r.greyBg : '#fff'
  const {
    masquaradeLogin,
    isCanMasqueradeLoading,
    canUseMasquerade,
    masquaradeUsersList,
    isMasquaradeUsersListLoading
  } = useMasquarade()

  const { masquaradeUser, isMasquarade, clearMasquaradeUser } = useMasquaradeUser()
  const { clearMasquaradeToken } = useMasquaradeToken()

  const form = useFormik({
    initialValues: {
      email: '',
      password: ''
    },
    validateOnChange: false,
    validateOnBlur: false,
    validationSchema: Yup.object().shape({
      email: Yup.string()
        .email(
          formatMessage({
            id: 'validation.email',
            defaultMessage: 'Niepoprawny adres email'
          })
        )
        .required(
          formatMessage({
            id: 'validation.required',
            defaultMessage: 'Wymagane'
          })
        ),
      password: Yup.string().required(
        formatMessage({
          id: 'validation.required',
          defaultMessage: 'Wymagane'
        })
      )
    }),
    onSubmit: async (values, formHelpers) => {
      formHelpers.setSubmitting(true)
      try {
        const { data } = await masquaradeLogin(values)
        enqueueSnackbar(
          formatMessage(
            {
              id: 'masquaradeLogin.success',
              defaultMessage: 'Zostałeś zalogwany jak {user}.'
            },
            { user: data.user.email }
          ),
          { variant: 'success' }
        )
        navigation('/user/account')
      } catch {
        enqueueSnackbar(
          formatMessage({
            id: 'masquaradeLogin.error',
            defaultMessage: 'Błąd logowania. Podano nieprawidłowy login lub hasło.'
          }),
          { variant: 'error' }
        )
      }
      formHelpers.setSubmitting(false)
    }
  })

  const pageLabel = formatMessage({
    id: 'masquaradeListItem.masquarade',
    defaultMessage: 'Maskarada'
  })

  const wrap = (content: ReactNode) => {
    if (isDesktop) return content
    return (
      <>
        <BackNavigationHeader label={pageLabel} />
        <BackNavigationContent>{content}</BackNavigationContent>
      </>
    )
  }

  if (!isCanMasqueradeLoading && !canUseMasquerade) {
    return <Navigate to='/user/account' />
  }

  if (isMasquarade) {
    return wrap(
      <Box sx={{ width: '100%', maxWidth: '560px' }}>
        {isDesktop && (
          <Typography sx={{ fontSize: '18px', fontWeight: 800, color: theme.r4r.navy, mb: '28px' }}>
            {formatMessage({
              id: 'masquaradeLogin.userInfo',
              defaultMessage: 'Maskarada jako:'
            })}
          </Typography>
        )}
        {!isDesktop && (
          <Typography sx={{ fontSize: '15px', fontWeight: 600, color: theme.r4r.ink, mb: '20px' }}>
            {formatMessage({
              id: 'masquaradeLogin.userInfo',
              defaultMessage: 'Maskarada jako:'
            })}
          </Typography>
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <TextField
            name='email'
            value={masquaradeUser.email}
            fullWidth
            label={formatMessage({
              id: 'masquaradeLogin.form.email',
              defaultMessage: 'Email'
            })}
            InputLabelProps={{ shrink: true }}
            disabled
            sx={profileFieldSx(true, labelBg)}
          />
          {!!masquaradeUser.firstName.length && (
            <TextField
              name='firstName'
              value={masquaradeUser.firstName}
              fullWidth
              label={formatMessage({
                id: 'masquaradeLogin.form.firstName',
                defaultMessage: 'Imię'
              })}
              InputLabelProps={{ shrink: true }}
              disabled
              sx={profileFieldSx(true, labelBg)}
            />
          )}
          {!!masquaradeUser.lastName.length && (
            <TextField
              name='lastName'
              value={masquaradeUser.lastName}
              fullWidth
              label={formatMessage({
                id: 'masquaradeLogin.form.lastName',
                defaultMessage: 'Nazwisko'
              })}
              InputLabelProps={{ shrink: true }}
              disabled
              sx={profileFieldSx(true, labelBg)}
            />
          )}
        </Box>
        <Button
          fullWidth
          startIcon={<ExitToApp sx={{ fontSize: '16px' }} />}
          onClick={() => {
            form.resetForm()
            clearMasquaradeUser()
            clearMasquaradeToken()
          }}
          sx={{
            mt: '32px',
            backgroundColor: theme.r4r.red,
            color: '#fff',
            borderRadius: 0,
            boxShadow: 'none',
            fontSize: '13px',
            fontWeight: 700,
            letterSpacing: '0.5px',
            textTransform: 'uppercase',
            py: '14px',
            ':hover': { backgroundColor: '#DC2626', boxShadow: 'none' }
          }}>
          {formatMessage({
            id: 'masquaradeLogin.logout',
            defaultMessage: 'Usuń maskaradę'
          })}
        </Button>
      </Box>
    )
  }

  if (!form.values.email) {
    if (isMasquaradeUsersListLoading) {
      return wrap(
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: '48px' }}>
          <CircularProgress />
        </Box>
      )
    }

    if (masquaradeUsersList.length) {
      return wrap(
        <Box>
          {isDesktop && (
            <Typography
              sx={{ fontSize: '18px', fontWeight: 800, color: theme.r4r.navy, mb: '28px' }}>
              {pageLabel}
            </Typography>
          )}
          <Typography sx={{ fontSize: '13px', color: theme.r4r.grey, mb: '16px' }}>
            {formatMessage({
              id: 'masquaradeLogin.selectUser',
              defaultMessage: 'Wybierz użytkownika, na którym chcesz zastosować maskaradę:'
            })}
          </Typography>
          <Box>
            {masquaradeUsersList.map(({ email }, index) => (
              <ButtonBase
                key={email}
                onClick={() => form.setFieldValue('email', email)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                  textAlign: 'left',
                  backgroundColor: '#fff',
                  px: '24px',
                  py: '21px',
                  borderTop: index === 0 ? `1px solid ${theme.r4r.greyLight}` : 'none',
                  borderBottom: `1px solid ${theme.r4r.greyLight}`,
                  ':hover': { backgroundColor: 'rgba(31, 31, 115, 0.04)' }
                }}>
                <Typography sx={{ fontSize: '15px', fontWeight: 600, color: theme.r4r.ink }}>
                  {email}
                </Typography>
                <KeyboardArrowRight sx={{ color: theme.r4r.grey, fontSize: '18px', ml: '16px' }} />
              </ButtonBase>
            ))}
          </Box>
        </Box>
      )
    }

    return wrap(
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: { xs: '40vh', md: 'calc(100vh - 140px)' },
          textAlign: 'center',
          px: '16px'
        }}>
        <Box
          sx={{
            width: '120px',
            height: '120px',
            borderRadius: '60px',
            backgroundColor: 'rgba(31, 31, 115, 0.08)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: '24px'
          }}>
          <People sx={{ fontSize: '56px', color: theme.r4r.navy }} />
        </Box>
        <Typography
          sx={{
            maxWidth: '520px',
            fontSize: '14px',
            color: theme.r4r.grey,
            lineHeight: '24px'
          }}>
          {formatMessage({
            id: 'masquaradeLogin.supervisorAccount',
            defaultMessage:
              'Brak użytkowników na których możesz zastosować maskaradę. Zaloguj się do CRM i ustaw hasło tymczasowe dla wybranego użytkownika.'
          })}
        </Typography>
      </Box>
    )
  }

  return wrap(
    <Box
      component='form'
      onSubmit={form.handleSubmit}
      sx={{ width: '100%', maxWidth: '560px' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          mb: '28px',
          ml: { md: '-8px', xs: 0 }
        }}>
        <IconButton
          onClick={() => form.setFieldValue('email', '')}
          sx={{ width: '32px', height: '32px', p: 0, borderRadius: 0, color: theme.r4r.ink }}>
          <ArrowBack sx={{ fontSize: '22px' }} />
        </IconButton>
        <Typography sx={{ fontSize: '15px', fontWeight: 600, color: theme.r4r.ink }}>
          {formatMessage({
            id: 'masquaradeLogin.backToList',
            defaultMessage: 'Wróć do listy'
          })}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        <TextField
          name='email'
          value={form.values.email}
          onChange={form.handleChange}
          fullWidth
          label={formatMessage({
            id: 'masquaradeLogin.form.email',
            defaultMessage: 'Email'
          })}
          InputLabelProps={{ shrink: true }}
          error={!!form.errors.email}
          helperText={form.errors.email}
          disabled
          sx={profileFieldSx(true, labelBg)}
        />
        <PasswordField
          name='password'
          value={form.values.password}
          onChange={form.handleChange}
          fullWidth
          label={formatMessage({
            id: 'masquaradeLogin.form.password',
            defaultMessage: 'Hasło'
          })}
          disabled={form.isSubmitting}
          error={!!form.errors.password}
          helperText={form.errors.password}
          sx={profileFieldSx(false, labelBg)}
        />
      </Box>
      <LoadingButton
        fullWidth
        type='submit'
        loading={form.isSubmitting}
        disabled={form.isSubmitting}
        sx={{ ...azureButtonSx, mt: '32px' }}>
        {formatMessage({
          id: 'masquaradeLogin.form.submit',
          defaultMessage: 'Zaloguj się'
        })}
      </LoadingButton>
    </Box>
  )
}

export default MasquaradeLogin
